/**
 * Two-tab area (Markets | Results) for baseball and racing.
 * Markets tab shows the given children (event selector + BetsArea for selected event).
 * Results tab shows schedule-scoped results via GenericScheduleResultsTab.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import GenericScheduleResultsTab from 'features/markets/components/bets-area/GenericScheduleResultsTab';
import { getSportColor, getSportLightColor, getSportOnColor } from 'shared/utils/sport-colors';
import { RtpTransparencyModal } from 'features/rtp/components/RtpTransparencyModal';
import { cn } from 'lib/utils';

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];

export default function ScheduleAreaTwoTabs({ sportId, children, event }) {
  const { t } = useTranslation();
  const [isRtpOpen, setIsRtpOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const sportLightColor = getSportLightColor(sportId);
  const sportColor = getSportColor(sportId);
  const sportOnColor = getSportOnColor(sportId);

  const scheduleTabClass = (selected) => cn(
    'py-2 md:py-3 text-center cursor-pointer transition-colors text-xs md:text-sm rounded-md border-[3px]',
    selected
      ? 'border-[var(--sport-color)] bg-card text-foreground'
      : 'border-border bg-card text-foreground hover:border-[var(--sport-color)]'
  );
  const isRacing = sportId != null && RACING_SPORT_IDS.includes(sportId);
  const isBaseball = sportId === 40;
  const eventId = event?.event_id;
  const secondLine = eventId != null && (isRacing || isBaseball)
    ? isRacing
      ? (event?.n_runners != null ? `#${eventId} • ${event.n_runners} ${t('RUNNERS') || 'runners'}` : `#${eventId}`)
      : `#${eventId}${event?.batter_name ? ` • ${event.batter_name}` : ''}`
    : null;

  return (
    <div className="w-full flex flex-col min-h-0 flex-1" style={{ '--sport-color': sportColor, '--sport-light-color': sportLightColor, '--sport-on-color': sportOnColor }}>
      <Tab.Group as="div" selectedIndex={selectedIndex} onChange={setSelectedIndex} className="flex flex-col min-h-0 flex-1">
        <Tab.List className="grid grid-cols-2 gap-1 p-2 border-b border-border shrink-0">
            <Tab
              className={({ selected }) =>
                cn(
                  scheduleTabClass(selected),
                  'px-2 flex items-center justify-center gap-2 min-w-0 relative'
                )
              }
            >
              <div className="flex flex-col items-center justify-center min-w-0 flex-1 text-center">
                <span className="truncate block w-full font-bold uppercase">{t('MARKETS') || 'Markets'}</span>
                {secondLine != null && (
                  <span className="block w-full text-[10px] md:text-xs font-normal opacity-90 truncate">{secondLine}</span>
                )}
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIsRtpOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setIsRtpOpen(true); } }}
                className="absolute right-2 top-1/2 -translate-y-1/2 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--sport-color)] hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset"
                aria-label={t('RTP_TRANSPARENCY_TITLE') || 'Winning probability (RTP)'}
                title={t('RTP_TRANSPARENCY_TITLE') || 'Winning probability (RTP)'}
              >
                <i className="bi bi-info-circle text-lg" aria-hidden="true" />
              </span>
            </Tab>
            <Tab className={({ selected }) => scheduleTabClass(selected)}>
              <span className="truncate block px-1 font-bold uppercase">{t('RESULTS') || 'Results'}</span>
            </Tab>
          </Tab.List>
          <RtpTransparencyModal
            open={isRtpOpen}
            onClose={() => setIsRtpOpen(false)}
            scheduleId={event?.schedule_id}
            sportId={sportId}
          />
        <Tab.Panels className="flex-1 min-h-0 flex flex-col">
          <Tab.Panel className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset focus-visible:ring-offset-0 rounded min-h-0 flex-1 flex flex-col overflow-auto">
            {children}
          </Tab.Panel>
          <Tab.Panel className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset focus-visible:ring-offset-0 rounded min-h-[200px] flex-1 flex flex-col overflow-auto">
            {selectedIndex === 1 ? <GenericScheduleResultsTab sportId={sportId} /> : null}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
